<script setup lang="ts">
import type { Themeable } from '#theme'
import { computed, useDefaults, useTheme } from '#imports'

export interface UIUserAvatarProps extends Themeable {
    displayName?: string
    username?: string
    size?: 'sm' | 'md' | 'lg'
    as?: 'button' | 'span'
}

const _props = withDefaults(defineProps<UIUserAvatarProps>(), {
    displayName: '',
    username: '',
    size: 'sm',
    as: 'button',
    color: undefined,
})

const props = useDefaults({
    props: _props,
    defaults: {
        color: 'primary',
    },
})

const { themeClass } = useTheme(() => ({ color: props.color }))

const displayInitials = computed<string>(() => {
    const fromName = props.displayName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    return fromName || props.username.slice(0, 2).toUpperCase()
})
</script>

<template>
    <component
        :is="props.as"
        :type="props.as === 'button' ? 'button' : undefined"
        class="user-avatar"
        :class="[`user-avatar--${props.size}`, themeClass]"
    >
        {{ displayInitials }}
    </component>
</template>

<style lang="scss" scoped>
.user-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1 / 1;
    border-radius: $border-radius * 2;
    background-color: var(--color, var(--primary));
    color: var(--on-color, var(--on-primary));
    border: none;
    padding: 0;
    font-weight: $text-semi-bold;
    line-height: 1;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    transition:
        box-shadow 0.2s ease-in-out,
        transform 0.1s ease-in-out;
    user-select: none;

    &--sm {
        height: 36px;
        font-size: $font-size-xs;
    }

    &--md {
        height: 44px;
        font-size: $font-size-sm;
    }

    &--lg {
        height: 56px;
        font-size: $font-size-base;
    }

    &:hover {
        box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.17);
    }

    &:active {
        transform: scale(0.96);
    }

    &:focus-visible {
        outline: 2px solid var(--color, var(--primary));
        outline-offset: 2px;
    }
}
</style>
